import { useParams, Link } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import JsonLd from '@/components/feature/JsonLd';
import { useSEO } from '@/hooks/useSEO';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const article = useMemo(() => articles.find((a) => a.id === id), [id]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
      .slice(0, 3);
  }, [article]);

  const canonical = article
    ? `https://huoying-tongren.com/article/${article.id}`
    : 'https://huoying-tongren.com/';
  const keywords = article
    ? `${article.tags.join(',')},火影同人本子,火影忍者同人,火影同人创作`
    : '火影同人本子,火影忍者同人';

  useSEO(
    article ? `${article.title} | 火影同人本子` : '文章未找到 | 火影同人本子',
    article?.excerpt || '火影同人本子 - 火影忍者粉丝二次创作平台',
    keywords,
    canonical,
    'article',
    article?.coverImage || '',
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 bg-background-100 rounded-full">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">你访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      image: article.coverImage,
      author: {
        '@type': 'Person',
        name: article.author,
      },
      datePublished: article.date,
      publisher: {
        '@type': 'Organization',
        name: '火影同人本子',
        url: 'https://huoying-tongren.com/',
      },
      url: canonical,
      inLanguage: 'zh-CN',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': canonical,
      },
      keywords: article.tags.join(','),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://huoying-tongren.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: article.category,
          item: `https://huoying-tongren.com/category/${article.categorySlug}`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: article.title,
          item: canonical,
        },
      ],
    },
  ];

  return (
    <>
      <ScrollToTop />
      <JsonLd data={articleJsonLd} />
      <Navbar />
      <main className="pt-16 bg-background-50">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: article.category, path: `/category/${article.categorySlug}` },
            { label: article.title },
          ]}
        />

        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50">
                {article.category}
              </span>
              <span className="text-xs text-foreground-400">{article.date}</span>
            </div>

            <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views} 阅读
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-heart-line"></i>
                {article.likes} 赞
              </span>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </header>

          <div className="relative h-64 md:h-96 rounded-xl overflow-hidden mb-10">
            <img
              src={article.coverImage}
              alt={article.title}
              title={`${article.title} - 火影同人本子`}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
              [&_h2]:font-heading [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:text-foreground-950 [&_h2]:mt-10 [&_h2]:mb-4
              [&_p]:mb-4 [&_p]:leading-relaxed
              [&_strong]:text-foreground-900
              [&_ul]:mb-4 [&_li]:mb-1"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <footer className="mt-12 pt-8 border-t border-background-200">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-lg text-foreground-950">相关推荐</h3>
              <Link
                to={`/category/${article.categorySlug}`}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 whitespace-nowrap"
              >
                查看更多
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/article/${related.id}`}
                  className="group bg-background-100 rounded-xl overflow-hidden hover:ring-2 hover:ring-primary-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative h-36 overflow-hidden">
                    <img
                      src={related.coverImage}
                      alt={related.title}
                      title={`${related.title} - 火影同人本子`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-3">
                    <h4 className="font-heading text-sm text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {related.title}
                    </h4>
                    <p className="text-xs text-foreground-500 mt-1">{related.author} · {related.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          </footer>
        </article>
      </main>
      <Footer />
    </>
  );
}